// Vendor Imports
import indexOf from 'lodash/indexOf';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import uniqueId from 'lodash/uniqueId';
import classNames from 'classnames';
import { useState } from 'react';
import { ForgeIcon, ForgeIconButton, ForgePopover, ForgeTooltip } from '@tylertech/forge-react';

// Project Imports
import { I18n } from 'common/i18n';
import { getNoopFilter } from 'common/components/FilterBar/lib/Filters/BaseFilter';
import { type SoqlFilter } from 'common/components/FilterBar/SoqlFilter';
import { type ViewColumn } from 'common/types/viewColumn';

const scope = 'shared.visualizations.charts.drill_down';

interface DrilldownPaneProps {
  columns?: ViewColumn[];
  currentDrilldownDimensionColumnName?: string;
  datasetUid?: string;
  drilldowns?: string[];
  filters?: SoqlFilter[];
  getDisplayNameForColumn?: (column: string) => string;
  isEditMode?: boolean;
  onDrilldownChange?: (filters: SoqlFilter[], drilldownDimensionColumnName?: string) => void;
  onDrilldownReset?: (filters: SoqlFilter[], drilldownDimensionColumnName?: string) => void;
  shouldDisableResetButton?: boolean;
}

export const DrilldownPane = ({
  columns = [],
  currentDrilldownDimensionColumnName,
  datasetUid = '',
  drilldowns = [],
  filters = [],
  getDisplayNameForColumn = () => '',
  isEditMode = true,
  onDrilldownChange = noop,
  onDrilldownReset = noop,
  shouldDisableResetButton,
}: DrilldownPaneProps) => {
  const [showHierarchyListContainer, setShowHierarchyListContainer] = useState(false);

  // Forge resolves popover/tooltip anchors with `querySelector('#id')`, which returns the first
  // match in the document. Multiple DrilldownPanes can coexist (for example, AX alongside the
  // underlying visualization), so the anchor ids must be unique per instance or the popover attaches
  // to the wrong button and never opens.
  const [uid] = useState(() => uniqueId());
  const drillUpButtonId = `drill-up-button-${uid}`;
  const hierarchyButtonId = `hierarchy-button-${uid}`;
  const resetButtonId = `reset-button-${uid}`;

  const isFirstDrilldownDimension = () => {
    return indexOf(drilldowns, currentDrilldownDimensionColumnName) === 0;
  };

  const getPreviousDrilldownDimension = () => {
    const drilldownOwnIndex = indexOf(drilldowns, currentDrilldownDimensionColumnName);
    return drilldowns[drilldownOwnIndex - 1] ?? currentDrilldownDimensionColumnName;
  };

  const getDrillDownFilters = () => {
    return filters.filter((filterItem) => drilldowns.includes(filterItem.columns[0]?.fieldName));
  };

  const onClickDrilldownReset = () => {
    const normalFilters = filters.filter((filterItem) => !filterItem.isDrilldown);
    const drilldownFilters = drilldowns.map((drilldown) => {
      const filterForColumn = filters.find((filterItem) => filterItem.columns[0]?.fieldName === drilldown);
      const isHidden = filterForColumn?.isHidden ?? false;
      const defaultFilterForColumn = getNoopFilter(
        { [datasetUid]: columns.find((column) => column.fieldName === drilldown)! },
        true,
      );
      return { ...defaultFilterForColumn, isHidden };
    });

    onDrilldownReset([...drilldownFilters, ...normalFilters], drilldowns[0]);
  };

  const onClickDrillUp = () => {
    const drilldownFilters = getDrillDownFilters();
    const currentDrilldownDimensionFilter = drilldownFilters.find(
      (filterItem) => filterItem.columns[0]?.fieldName === currentDrilldownDimensionColumnName,
    );

    const previousDrilldownDimension = isEmpty(currentDrilldownDimensionFilter?.arguments)
      ? getPreviousDrilldownDimension()
      : currentDrilldownDimensionColumnName;

    const newFilters = filters.map((filterItem) => {
      if (filterItem.columns[0]?.fieldName === previousDrilldownDimension) {
        const isHidden = filterItem.isHidden ?? false;
        const defaultFilterForColumn = getNoopFilter(
          { [datasetUid]: columns.find((column) => column.fieldName === previousDrilldownDimension)! },
          true,
        );
        return { ...defaultFilterForColumn, isHidden };
      }
      return filterItem;
    });

    onDrilldownChange(newFilters, previousDrilldownDimension);
  };

  const onDimensionChange = (nextDrilldownDimension: string) => {
    onDrilldownChange(filters, nextDrilldownDimension);
    setShowHierarchyListContainer(false);
  };

  const renderHierarchyList = () => {
    return drilldowns.map((drilldownColumnName, index) => {
      const isActive = drilldownColumnName === currentDrilldownDimensionColumnName;
      const listClasses = classNames('drill-down-list', { active: isActive });

      return (
        <li key={index} className={listClasses}>
          <button
            type="button"
            className="drill-down-list-button"
            aria-current={isActive ? 'true' : undefined}
            data-testid={`drill-down-list-button-${drilldownColumnName}`}
            onClick={() => onDimensionChange(drilldownColumnName)}
          >
            {getDisplayNameForColumn(drilldownColumnName)}
          </button>
        </li>
      );
    });
  };

  const drilldownFiltersWithArguments = getDrillDownFilters().filter(
    (filterItem) => filterItem.arguments != null,
  );
  const disabledResetButton = isEditMode
    ? isFirstDrilldownDimension() && isEmpty(drilldownFiltersWithArguments)
    : shouldDisableResetButton;

  return (
    <div className="drill-down-btn-group">
      <ForgeIconButton
        aria-label={I18n.t('drill_up.title', { scope })}
        dense
        data-testid="drill-up-button"
        disabled={isFirstDrilldownDimension()}
        id={drillUpButtonId}
        onClick={onClickDrillUp}
        shape="squared"
      >
        <ForgeIcon name="north" />
      </ForgeIconButton>
      <ForgeTooltip anchor={drillUpButtonId} placement="bottom">
        {I18n.t('drill_up.title', { scope })}
      </ForgeTooltip>

      <ForgeIconButton
        aria-label={I18n.t('hierarchy.title', { scope })}
        dense
        data-testid="hierarchy-button"
        id={hierarchyButtonId}
        shape="squared"
      >
        <ForgeIcon name="transit_connection" />
      </ForgeIconButton>
      <ForgeTooltip anchor={hierarchyButtonId} placement="bottom">
        {I18n.t('hierarchy.title', { scope })}
      </ForgeTooltip>

      <ForgeIconButton
        aria-label={I18n.t('reset.title', { scope })}
        dense
        data-testid="reset-button"
        disabled={disabledResetButton}
        id={resetButtonId}
        onClick={onClickDrilldownReset}
        shape="squared"
      >
        <ForgeIcon name="refresh" />
      </ForgeIconButton>
      <ForgeTooltip anchor={resetButtonId} placement="bottom">
        {I18n.t('reset.title', { scope })}
      </ForgeTooltip>

      <ForgePopover
        anchor={hierarchyButtonId}
        open={showHierarchyListContainer}
        on-forge-popover-toggle={(evt: CustomEvent<{ newState: string }>) => {
          setShowHierarchyListContainer(evt.detail.newState === 'open');
        }}
        placement="bottom-start"
      >
        <div className="hierarchy-container">
          <div className="hierarchy-list">
            <ul>{renderHierarchyList()}</ul>
          </div>
        </div>
      </ForgePopover>
    </div>
  );
};
